"use client";

import { useCallback, useEffect, useState } from "react";
import { Circle, Coffee, CheckCircle2 } from "lucide-react";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { setBreakState, clearBreakState } from "@/lib/chat/break-state";
import {
  setMyChatwootAgentId,
  MY_AVAILABILITY_EVENT,
  type Availability as MyAvailability,
} from "@/lib/chat/my-availability";

type Availability = "online" | "busy" | "offline" | "on_break";

interface ActiveBreak {
  id: number;
  breakReasonId: number | null;
  breakReasonName: string | null;
  startedAt: string;
}

interface StatusSnapshot {
  availability: Availability;
  activeBreak: ActiveBreak | null;
  agentId: number;
}

interface BreakReason {
  id: number;
  name: string;
  active: boolean;
  position: number;
}

const STATUS_LABEL: Record<Availability, string> = {
  online: "Online",
  busy: "Ocupado",
  on_break: "Em pausa",
  offline: "Offline",
};

const STATUS_DOT_CLASS: Record<Availability, string> = {
  online: "bg-emerald-500",
  busy: "bg-amber-500",
  on_break: "bg-orange-500",
  offline: "bg-muted-foreground",
};

/**
 * Seletor de status do PROPRIO agente, dentro do dropdown de perfil da sidebar.
 * Paridade fork: SidebarProfileMenuStatus.vue (dropdown online/ocupado +
 * iniciar pausa com motivos / retomar). Status atual hidratado via
 * GET /chat/me/availability no mount + atualizado em tempo real pelo evento
 * `gk:chat:my-availability` (derivado do payload de presenca — #102, zero GET).
 */
export function SidebarAgentStatus() {
  const [status, setStatus] = useState<StatusSnapshot | null>(null);
  const [reasons, setReasons] = useState<BreakReason[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const snap = await api.request<StatusSnapshot>("/chat/me/availability");
      setStatus(snap);
      setMyChatwootAgentId(snap.agentId);
    } catch {
      // 404 (agente sem ChatAgentSync, ex: gk-admin) — esconde silencioso
      setStatus(null);
      setMyChatwootAgentId(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Carrega motivos uma vez (mudam raramente). Falha silenciosa.
  useEffect(() => {
    api
      .request<BreakReason[]>("/chat/me/break-reasons")
      .then(setReasons)
      .catch(() => setReasons([]));
  }, []);

  // #102: status proprio em tempo real vem do payload de presenca (paridade
  // fork auth.js:212), normalizado em gk:chat:my-availability. Zero GET.
  useEffect(() => {
    const onMyAvailability = (e: Event) => {
      const av = (e as CustomEvent<{ availability: MyAvailability }>).detail?.availability;
      if (!av) return;
      // Ao sair de on_break, limpa o activeBreak (o detalhe da pausa nao vem no
      // payload de presenca; quando entra em pausa, o motivo vem do POST/localStorage).
      setStatus((prev) =>
        prev
          ? { ...prev, availability: av, activeBreak: av === "on_break" ? prev.activeBreak : null }
          : prev,
      );
    };
    window.addEventListener(MY_AVAILABILITY_EVENT, onMyAvailability);
    return () => window.removeEventListener(MY_AVAILABILITY_EVENT, onMyAvailability);
  }, []);

  const setAvailability = async (availability: "online" | "busy" | "offline") => {
    if (busy) return;
    setBusy(true);
    try {
      const snap = await api.request<StatusSnapshot>("/chat/me/availability", {
        method: "POST",
        body: JSON.stringify({ availability }),
      });
      setStatus(snap);
      toast.success(`Status: ${STATUS_LABEL[availability]}`);
    } catch (err) {
      toast.error("Falha ao alterar status.");
    } finally {
      setBusy(false);
    }
  };

  const startBreak = async (breakReasonId: number) => {
    if (busy) return;
    setBusy(true);
    try {
      const snap = await api.request<StatusSnapshot>("/chat/me/breaks/start", {
        method: "POST",
        body: JSON.stringify({ breakReasonId }),
      });
      setStatus(snap);
      setBreakState({
        reason: snap.activeBreak?.breakReasonName ?? null,
        startedAt: snap.activeBreak?.startedAt ?? new Date().toISOString(),
        minimized: false,
      });
      toast.success(`Pausa iniciada: ${snap.activeBreak?.breakReasonName ?? ""}`);
    } catch {
      toast.error("Falha ao iniciar pausa.");
    } finally {
      setBusy(false);
    }
  };

  const endBreak = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const snap = await api.request<StatusSnapshot>("/chat/me/breaks/end", {
        method: "POST",
      });
      setStatus(snap);
      clearBreakState();
      toast.success("Pausa encerrada.");
    } catch {
      toast.error("Falha ao encerrar pausa.");
    } finally {
      setBusy(false);
    }
  };

  // Agente sem vinculo (gk-admin / user nao-agente): nao mostra o seletor.
  if (!status) return null;

  const current = status.availability;
  const inBreak = current === "on_break";

  return (
    <>
      <div className="px-2 py-1.5 text-[11px] text-muted-foreground flex items-center gap-2">
        <Circle className={`h-2 w-2 rounded-full ${STATUS_DOT_CLASS[current]}`} fill="currentColor" />
        Status: <span className="font-medium text-foreground">{STATUS_LABEL[current]}</span>
        {inBreak && status.activeBreak?.breakReasonName && (
          <span className="text-muted-foreground/70">— {status.activeBreak.breakReasonName}</span>
        )}
      </div>
      <DropdownMenuSeparator />

      {inBreak ? (
        <DropdownMenuItem onClick={endBreak} disabled={busy}>
          <CheckCircle2 className="h-4 w-4 mr-2" /> Retomar trabalho
        </DropdownMenuItem>
      ) : (
        <>
          <DropdownMenuItem onClick={() => setAvailability("online")} disabled={busy || current === "online"}>
            <Circle className="h-3 w-3 mr-2 text-emerald-500" fill="currentColor" /> Online
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setAvailability("busy")} disabled={busy || current === "busy"}>
            <Circle className="h-3 w-3 mr-2 text-amber-500" fill="currentColor" /> Ocupado
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger disabled={busy || reasons.length === 0}>
              <Coffee className="h-4 w-4 mr-2" /> Iniciar pausa
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {reasons.length === 0 ? (
                <DropdownMenuItem disabled>Nenhum motivo cadastrado</DropdownMenuItem>
              ) : (
                reasons.map((r) => (
                  <DropdownMenuItem key={r.id} onClick={() => startBreak(r.id)}>
                    {r.name}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </>
      )}
      <DropdownMenuSeparator />
    </>
  );
}
